import { AfterViewInit, Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import { BaseComponent } from '../../../../shared/components/base/base.component';

export interface ColumnSpecs {
    height: number;
    label: number;
    value: number;
    total: number;
    ratio?: number;
}

@Component({
    selector: 'result-graphic',
    templateUrl: './result-graphic.component.html',
    styleUrls: ['./result-graphic.component.scss']
})
export class ResultGraphicComponent extends BaseComponent
                                    implements OnChanges, AfterViewInit {

    @ViewChild('repairGraph', { static: true })
    public repairGraph: ElementRef;

    @ViewChild('repairGraphSeparator', { static: true })
    public repairGraphSeparator: ElementRef;

    @ViewChild('replacementGraph', { static: true })
    public replacementGraph: ElementRef;

    @ViewChild('salvageGraph', { static: true })
    public salvageGraph: ElementRef;

    public _claimAmount: number;
    public _replacementAmount: number;
    private _reducedAmount: number;
    private _salvageAmount: number;

    @Input()
    set claimAmount(value: number) {
        this._claimAmount = value;
    }
    get claimAmount() {
        return this._claimAmount || 0;
    }
    @Input()
    set replacementAmount(value: number) {
        this._replacementAmount = value;
    }
    get replacementAmount() {
        return this._replacementAmount || 0;
    }

    @Input()
    set reducedAmount(value: number) {
        this._reducedAmount = value;
    }
    get reducedAmount() {
        return this._reducedAmount || 0;
    }

    @Input()
    set salvageAmount(value: number) {
        this._salvageAmount = value;
    }
    get salvageAmount() {
        return this._salvageAmount || 0;
    }

    /**
     * Chart columns configuration
     *
     * height - CSS-related height
     * label - Percentage value to show as column label
     * value - User-provided value (gross price)
     * ratio - Only in the first column, ratio between claim amount
     * and total repair amount
     */

    public columns: {
        repair: ColumnSpecs,
        replacement: ColumnSpecs,
        salvage: ColumnSpecs,
    };

    private viewReady = false;

    get hasRequiredParameters(): boolean {
        return !!(this.viewReady && this.claimAmount || this.replacementAmount);
    }

    constructor() {
        super();
        this.initColumns();
    }

    private initColumns(): void {
        this.columns = {
            repair: {
                height: 0,
                label: 0,
                value: 0,
                total: 0,
                ratio: 0
            },
            replacement: {
                height: 0,
                label: 100,
                value: 0,
                total: 0
            },
            salvage: {
                height: 0,
                label: 0,
                value: 0,
                total: 0
            }
        };
    }

    private processData(): void {
        this.calculateRatio();
        this.calculateHeight();
        this.calculateLabels();
        this.updateChart();
    }

    /**
     * Calculates the reduce to claim ratio
     *
     * For the first column, we need to return the ratio between
     * claim amount and total repair amount. Please note, as reducedAmount
     * is not a required field, its value is zero by default.
     */
    private calculateRatio(): void {
        this.columns.repair.total = this.reducedAmount + this.claimAmount;

        if (this.columns.repair.total !== 0) {
            this.columns.repair.ratio = (this.claimAmount / this.columns.repair.total) * 100;
        }

        this.columns.replacement.total = this.replacementAmount;
        this.columns.salvage.total = this.salvageAmount;
    }

    /**
     * Calculate columns height
     *
     * Provided the replacement column is always 100%, we need to
     * calculate the actual, CSS-related, height values, which will
     * be used to display the columns.
     *
     * Usually the replacement column will be highest (tollest), but
     * there are cases (with huge dammages) when the repair could be
     * more expensive. In this case the first column will get CSS
     * height = 100% and the replacement column will be smaller, but
     * still labeled as 100%.
     */
    private calculateHeight(): void {
        if (this.columns.repair.total < this.columns.replacement.total) {
            this.columns.repair.height = Math.round((this.columns.repair.total / this.columns.replacement.total) * 100);
            this.columns.replacement.height = 100;
            this.columns.salvage.height = Math.round((this.columns.salvage.total / this.columns.replacement.total) * 100);
        } else if (this.columns.repair.total > 0) {
            this.columns.replacement.height = Math.round((this.columns.replacement.total / this.columns.repair.total) * 100);
            this.columns.repair.height = 100;
        } else {
            this.columns.replacement.height = 0;
            this.columns.salvage.height = 0;
            this.columns.repair.height = 0;
        }

        if (this.columns.replacement.total > 0) {
            this.columns.salvage.height = this.columns.replacement.height * (this.columns.salvage.total / this.columns.replacement.total);
        } else if (this.columns.repair.total > 0) {
            this.columns.salvage.height = this.columns.repair.height * (this.columns.salvage.total / this.columns.repair.total);
        } else if (this.columns.replacement.total === 0 && this.columns.salvage.total > 0) {
            this.columns.salvage.height = 100;
        } else {
            this.columns.salvage.height = 0;
        }
    }

    /**
     * Calculate the percentage values, used as labels
     */
    private calculateLabels(): void {
        if (this.columns.replacement.total > 0) {
            this.columns.replacement.label = 100;
        } else {
            this.columns.replacement.label = 0;
        }

        if (this.columns.repair.total < this.columns.replacement.total) {
            this.columns.repair.label = this.columns.repair.height;
        } else if (this.columns.replacement.total > 0) {
            this.columns.repair.label = Math.round((this.columns.repair.total / this.columns.replacement.total) * 100);
        } else if (this.columns.repair.total > 0) {
            this.columns.repair.label = 100;
        } else {
            this.columns.repair.label = 0;
        }

        if (this.columns.replacement.total > 0) {
            this.columns.salvage.label = Math.round((this.columns.salvage.total / this.columns.replacement.total) * 100);
        } else if (this.columns.repair.total > 0) {
            this.columns.salvage.label = Math.round((this.columns.salvage.total / this.columns.repair.total) * 100);
        } else if (this.columns.replacement.total === 0 && this.columns.salvage.total > 0) {
            this.columns.salvage.label = 100;
        } else {
            this.columns.salvage.label = 0;
        }
    }

    /**
     * Update chart columns heights
     */

    private updateChart(): void {
        this.repairGraph.nativeElement.style.height = this.columns.repair.height + '%';
        this.repairGraphSeparator.nativeElement.style.bottom = this.columns.repair.ratio + '%';
        this.replacementGraph.nativeElement.style.height = this.columns.replacement.height + '%';
        this.salvageGraph.nativeElement.style.height = this.columns.salvage.height + '%';
    }

    public ngOnChanges() {
        if (this.hasRequiredParameters) {
            this.initColumns();
            this.processData();
        }
    }

    public ngAfterViewInit() {
        this.viewReady = true;

        setTimeout(() => {
            this.ngOnChanges();
        });
    }
}
